import { showBusinessError } from '@tencent/wphk-components-dialog';
import { to } from '@tencent/wphk-base-utils';
import { computed, ref, Ref } from 'vue-demi';
import {
  initActivity,
  getCouponList,
  reqryCoupon,
  receiveCoupon,
  filterActIdsByBlackUin,
} from '../business/activity';
import { CustomException } from '@tencent/ppd-base-libs';
import { ItemInfo, FixedCouponType, ConfigCouponInfo, ValidConfigItem } from '../types';
import { sortCoupon } from '../business';
import { i18n } from '../i18n';
import { getConfigById } from '../business/config';
import { DistributionActivity } from '@tencent/wphk-act-base-framework';

export function useScanGift() {
  // ---- data ----
  /** 活动实例 */
  const activityObj: Ref<DistributionActivity | null> = ref(null);
  // 根据campaignId/spId筛选后的券礼包配置
  const validFixedScanGift: Ref<ValidConfigItem | undefined> = ref();
  // 固定券礼包券详情列表
  const fixedCouponDetailList: Ref<ItemInfo[]> = ref([]);
  // 查询是否有报错
  const isQryOK = ref(true);
  // 如果有用户专属券，则查询黑白名单过滤，这是过滤后的券列表
  const finalCouponInfo: Ref<ConfigCouponInfo[]> = ref([]);

  // ---- computed ----
  // 条款与细则url - 从有效券礼包中获取
  const tncUrlForFixed = computed(() => validFixedScanGift.value?.tncUrl ?? '');
  // 换肤图片 - 从有效券礼包中获取
  const skinForFixed = computed(() => validFixedScanGift.value?.skin);
  // 模块标题 - 从有效券礼包中获取
  const titleForFixed = computed(() => validFixedScanGift.value?.title || i18n('掃碼專享禮包'));
  // 是否展示扫码券礼包模块（获取到固定券礼包-活动Id加密串，说明要展示券礼包）
  const isShowScanGift = computed(() => finalCouponInfo.value.length > 0 && isQryOK.value);
  const promptDialogPic = computed(() => validFixedScanGift.value?.promptDialogPic);

  /** 初始化，根据campaignId/spId获取配置 */
  async function initScanGift(campaignId: string, spId: string) {
    validFixedScanGift.value = await getConfigById(campaignId, spId);

    // 查询黑名名单过滤券
    finalCouponInfo.value = await handleInvitedCoupon(validFixedScanGift.value?.fixedCouponInfo ?? []);
    console.log('useScanGift 查询黑白名单后的券配置', finalCouponInfo.value);

    // 查券
    if (!finalCouponInfo.value.length) return;

    fixedCouponDetailList.value = await handleQryCoupon(finalCouponInfo.value);
    console.log('useScanGift 查询固定券详情列表', fixedCouponDetailList.value);
  }

  // ---- 辅助方法 ----
  // 查询黑白名单，过滤用户专属券，得到最后有效的券列表
  async function handleInvitedCoupon(couponInfo: ConfigCouponInfo[]): Promise<ConfigCouponInfo[]> {
    console.log('useScanGift couponInfo', couponInfo);
    const invitedUserCouponInfo = couponInfo.filter(it => it.type === FixedCouponType.invitedUser);

    console.log('useScanGift 用户专属券配置', invitedUserCouponInfo);
    let validInviteduserActIds: ConfigCouponInfo[] = [];
    if (invitedUserCouponInfo.length > 0) {
      // 查询黑白名单
      validInviteduserActIds = await filterActIdsByBlackUin(invitedUserCouponInfo);
    }

    console.log('useScanGift 有效的用户专属券', invitedUserCouponInfo);
    const otherCouponInfo = couponInfo.filter(it => it.type !== FixedCouponType.invitedUser);

    return [...otherCouponInfo, ...validInviteduserActIds];
  }
  // 查券
  async function handleQryCoupon(couponInfo: ConfigCouponInfo[]): Promise<ItemInfo[]> {
    const [err, res] = await to(initActivity(couponInfo.map(it => it.actIdEn)));
    if (err) {
      isQryOK.value = false;
      showBusinessError(err);
      new CustomException(err, 'InitDistributionActivityException', couponInfo.join(','));
      return [];
    }
    activityObj.value = res;
    // 获取券详情
    const [err2, couponArr] = await to(getCouponList(activityObj.value));
    if (err2) {
      isQryOK.value = false;
      showBusinessError(err2);
      new CustomException(err2, 'GetCouponListException', couponInfo.join(','));
      return [];
    }
    return sortCoupon(couponArr, couponInfo);
  }
  // 领券
  async function receiveFixedCoupon({
    enActid, prizeId, disSource }: { enActid: string; prizeId: string; disSource?: string }) {
    // 为了兼容推广员场景（传入disSource),会把spId字段格式化成：spId,disSource 逗号分割
    // disSource格式参考：promoter202403.xxxxxxxxxxxxxxxxxxxx(base64)
    let spId = validFixedScanGift.value?.spId;
    if (disSource) {
      spId = [spId, disSource].join(',');
    }
    return await receiveCoupon({ activityObj: activityObj.value, actListid: enActid, prizeId, spId });
  }
  // 领券后，更新券信息
  async function updateFixedCoupon(curItem?: ItemInfo) {
    await reqryCoupon(activityObj.value);
    // 更新当前的fixedCouponDetailList
    const couponArr = await getCouponList(activityObj.value);
    // fixedCouponDetailList.value = couponArr;
    // 要保持顺序fixedCouponDetailList.value顺序，只能遍历去更新
    const tmpList: ItemInfo[] = [];
    fixedCouponDetailList.value.forEach((item) => {
      const findItem = couponArr.find(it => it.couponId === item.couponId);
      if (!findItem) return;
      tmpList.push({ ...item, ...findItem });
    });
    fixedCouponDetailList.value = tmpList;
    console.log('useScanGift 更新固定券详情列表成功', fixedCouponDetailList.value);

    // 返回当前券最新信息
    if (curItem) {
      return fixedCouponDetailList.value.find(item => item.couponId === curItem.couponId);
    }
  }

  return {
    /** 是否展示扫码券礼包模块 */
    isShowScanGift,
    /** 固定券详情列表 */
    fixedCouponDetailList,
    /** 条款与细则url */
    tncUrlForFixed,
    /** 换肤图片 */
    skinForFixed,
    /** 模块标题 */
    titleForFixed,
    /** 弹窗图片 */
    promptDialogPic,
    /** 有效的券列表 */
    finalCouponInfo,

    /** 初始化 */
    initScanGift,
    /** 领券 */
    receiveFixedCoupon,
    /** 领券之后，更新券信息 */
    updateFixedCoupon,
  };
}
